import './Account.scss'
import React, { useState, useEffect } from 'react'
import { useConnect } from 'redux-bundler-hook'
import useTranslations from '@src/hooks/useTranslations'
import BEMHelper from 'react-bem-helper'
import { navigate } from 'gatsby'
import CircularProgress from '@material-ui/core/CircularProgress'
import Container from '@material-ui/core/Container'
import Divider from '@material-ui/core/Divider'
import AddDriversLicensePage from '@src/components/account/pages/AddDriversLicensePage'
import AddPaymentMethod from '@src/components/account/pages/AddPaymentMethod'
import ChooseAccountLevel from '@src/components/account/pages/ChooseAccountLevelPage'
import ConfirmDriversLicenseAndCreditCheckPage from '@src/components/account/pages/ConfirmDriversLicenseAndCreditCheckPage'
import ContactInformation from '@src/components/account/pages/ContactInformation'
import MobileNumberVerificationPage from '@src/components/account/pages/MobileNumberVerificationPage'
import SuccessPage from '@src/components/account/pages/SuccessPage'
import ErrorModal from '@src/components/ErrorModal'
import useSetting from '@src/hooks/useSetting'

const bem = new BEMHelper('registration')

export const REGISTRATION_STEPS = {
	CHOOSE_ACCOUNT_LEVEL: 1,
	CONTACT_INFORMATION: 2,
	VERIFY_MOBILE_NUMBER: 3,
	DRIVERS_LICENSE: 4,
	CONFIRM_DRIVERS_LICENSE_AND_CREDIT_CHECK: 5,
	SELECT_PAYMENT_METHOD: 6,
	SUCCESS_PAGE: 7,
}

const ACCOUNT_TRANSLATIONS = {
	'1': {
		title: 'Välj din prisnivå',
		description:
			'Vi kommer använda uppgifterna för att kunna kontakta dig, din e-postadress kommer att vara ditt användarnamn.',
	},
	'2': {
		title: 'kinto.register.contact-info-title',
		description: 'kinto.register.contact-info-description',
	},
	'3': {
		title: 'kinto.register.phone-verification-title',
		description: 'kinto.register.phone-verification-description',
	},
	'4': {
		title: 'kinto.register.drivers-license-title',
		description: 'kinto.register.drivers-license-description',
	},
	'5': {
		title: 'kinto.register.drivers-license-and-credit-check-title',
		description: 'kinto.register.drivers-license-and-credit-check-description',
		businessTitle:
			'kinto.register.drivers-license-and-credit-check-title.business',
		businessDescription:
			'kinto.register.drivers-license-and-credit-check-description.business',
	},
	'6': {
		title: 'kinto.register.payment-title',
		description: 'kinto.register.payment-description',
		businessDescription: 'kinto.register.payment-description.business',
	},
	'7': {
		title: 'kinto.register.welcome-title',
		description: 'kinto.register.welcome-description',
	},
}

const Account = ({ selectedPage = 2, customPageContext, location }) => {
	const t = useTranslations()
	const [currentStep, setCurrentStep] = useState(selectedPage)
	const [error, setError] = useState(null)
	const [loading, setLoading] = useState(false)
	const getSetting = useSetting()
	const salesforceLink = getSetting('salesforce_mypages')
	const {
		businessCustomer,
		isMissingInformation,
		isCustomerLoggedIn,
	} = useConnect(
		'selectBusinessCustomer',
		'selectIsMissingInformation',
		'selectIsCustomerLoggedIn',
	)

	useEffect(() => {
		setCurrentStep(selectedPage)
	}, [selectedPage])

	useEffect(() => {
		if (
			isCustomerLoggedIn &&
			isMissingInformation === false &&
			currentStep !== 6 &&
			currentStep !== 7
		) {
			if (salesforceLink === '') {
				navigate(location.pathname.replace('kom-igang', 'boka-bil'))
			} else {
				window.location.replace(salesforceLink)
			}
			return
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCustomerLoggedIn, isMissingInformation, currentStep])

	const renderCurrentRegistrationStep = () => {
		let Page = ContactInformation
		let props = {}
		switch (currentStep) {
			case REGISTRATION_STEPS.CONTACT_INFORMATION:
				Page = ContactInformation
				break
			case REGISTRATION_STEPS.CHOOSE_ACCOUNT_LEVEL:
				Page = ChooseAccountLevel
				break
			case REGISTRATION_STEPS.VERIFY_MOBILE_NUMBER:
				Page = MobileNumberVerificationPage
				break
			case REGISTRATION_STEPS.DRIVERS_LICENSE:
				Page = AddDriversLicensePage
				break
			case REGISTRATION_STEPS.CONFIRM_DRIVERS_LICENSE_AND_CREDIT_CHECK:
				Page = ConfirmDriversLicenseAndCreditCheckPage
				break
			case REGISTRATION_STEPS.SELECT_PAYMENT_METHOD:
				Page = AddPaymentMethod
				break
			case REGISTRATION_STEPS.SUCCESS_PAGE:
				Page = SuccessPage
				break
			default:
				Page = ContactInformation
				break
		}
		return (
			<Page
				setNextStep={setCurrentStep}
				setLoading={setLoading}
				setError={setError}
				customPageContext={customPageContext}
				{...props}
			/>
		)
	}

	return (
		<div {...bem('')}>
			<Container>
				<h3 {...bem('title')}>
					{currentStep === 5 && businessCustomer
						? t(ACCOUNT_TRANSLATIONS[currentStep].businessTitle)
						: t(ACCOUNT_TRANSLATIONS[currentStep].title || 'kinto.unkown')}
				</h3>
				{ACCOUNT_TRANSLATIONS[currentStep].description && (
					<p {...bem('description')}>
						{(currentStep === 6 || currentStep === 5) && businessCustomer
							? t(ACCOUNT_TRANSLATIONS[currentStep].businessDescription)
							: t(ACCOUNT_TRANSLATIONS[currentStep].description)}
					</p>
				)}
				<Divider />
				<div {...bem('container')}>{renderCurrentRegistrationStep()}</div>
			</Container>
			{loading && (
				<div {...bem('loading-container')}>
					<CircularProgress size='15rem' />
				</div>
			)}
			{error && (
				<ErrorModal
					onClose={() => {
						setError()
						if (
							currentStep ===
							REGISTRATION_STEPS.CONFIRM_DRIVERS_LICENSE_AND_CREDIT_CHECK
						) {
							setCurrentStep(REGISTRATION_STEPS.DRIVERS_LICENSE)
						}
						if (error.redirectOnClose) {
							navigate('/redirect?showLoginModal=true')
						}
					}}
					buttonLabel={error.buttonLabel}
				>
					{error.message}
				</ErrorModal>
			)}
		</div>
	)
}

export default Account
